/**
 * Extract values from cookies, return parsed values in an array
 * @param {array} cookies
 * @returns {array}
 */
const extractCookieValues = (cookies) => {
  const [hasAuthorizations, cmsInternalRoleValues] = cookies;
  const cmsInternalRoles = cmsInternalRoleValues
    ? JSON.parse(cmsInternalRoleValues)
    : null;
  return [hasAuthorizations, cmsInternalRoles];
};

const reviewerRoles = [
  "QPP Self-Nomination",
  "QPP Targeted Review & Exceptions",
  "QPP Case Management - PIMMS Reviewer",
  "QPP Case Management - ACO PAC Reviewer",
  "QPP Case Management - CMS Reviewer",
];

const cmsRoles = [
  "QPP Content Management - Author",
  "QPP Content Management - Approver",
  "QPP Content Management - Admin",
  "QPP Content Management - Previewer",
  "QPP Front-end - Author",
];

/**
 * Check if internal cms role is a Reviewer role
 * @param {string} cmsInternalRole
 * @returns {boolean}
 */
export const isReviewerRole = (cmsInternalRole) => {
  return reviewerRoles.includes(cmsInternalRole);
};

/**
 * Check if internal cms roles has Reviewer role
 * @param {array} cmsInternalRoles array of values
 * @returns {boolean}
 */
const hasReviewerRole = (cmsInternalRoles) => {
  if (!cmsInternalRoles) return false;
  return cmsInternalRoles.some(isReviewerRole);
};

/**
 * Check if internal cms roles includes Helpdek role
 * @param {array} cmsInternalRoles array of values
 * @returns {boolean}
 */
const hasHelpdeskRole = (cmsInternalRoles) => {
  if (!cmsInternalRoles) return false;
  return cmsInternalRoles.includes("QPP Helpdesk");
};

const hasContentMgmtRole = (cmsInternalRoles) => {
  if (!cmsInternalRoles) return false;
  return cmsInternalRoles.some((role) => cmsRoles.includes(role));
};

/**
 * Transforms cookie values into optios format for use in role seelction dropdown
 * @param {array} values extracted & parsed vookie values
 * @returns {array}
 */
const transformCookieValues = (values) => {
  const optionValues = [];
  const [hasAuthorizations, cmsInternalRoles] = values;

  if (hasAuthorizations === "true") {
    optionValues.push({ name: "QPP User", value: "QPP User" });
  }
  if (hasReviewerRole(cmsInternalRoles)) {
    optionValues.push({ name: "Reviewer", value: "Reviewer" });
  }
  if (hasHelpdeskRole(cmsInternalRoles)) {
    optionValues.push({ name: "Helpdesk Viewer", value: "Helpdesk Viewer" });
  }
  if (hasContentMgmtRole(cmsInternalRoles)) {
    optionValues.push({ name: "Content Manager", value: "Content Manager" });
  }
  return optionValues;
};

/**
 *
 * @param {array} cookies authz & cms_internal_authz values parsed from cookies
 * @returns {array} of objects for use in role dropdown
 */
export const loadRoleOptions = (cookies) => {
  const cookieValues = extractCookieValues(cookies);
  return transformCookieValues(cookieValues);
};

const defaultPageByRole = {
  "QPP User": "/user/submissions",
  "Helpdesk Viewer": "/user/helpdesk-viewing-tool",
  Reviewer: "/user/reviewers",
  "Content Manager": "/user/content-management",
};

/**
 * Get redirect url/page by role
 * @param {string} role
 * @returns {string}
 */
export const redirectPage = (role) => {
  const defaultPage = defaultPageByRole[role];
  const { pathname } = window.location;
  if (pathname === defaultPage) return;
  else window.location.assign(defaultPage);
};

/**
 * Get localStorage values related to user role
 * @returns {object}
 */
export const getLocalStorageRoleState = () => ({
  selectedUserRole: localStorage.getItem("selectedUserRole"),
});

/**
 * Initialize role values in localStorage
 * @param {string} roleValue selector role value from dropdown
 * @returns {void}
 */
export const initializeLocalStorageRoleState = (roleValue) => {
  const localStorageValues = getLocalStorageRoleState();

  if (!localStorageValues.selectedUserRole) {
    localStorage.setItem("selectedUserRole", roleValue);
  }
};

/**
 * Update role values in localStorage
 * @param {string} updatedRole new user role selected from dropdown
 */
export const updateLocalStorageRoleState = (updatedRole) =>
  localStorage.setItem("selectedUserRole", updatedRole);
